import React, { useState, useEffect } from "react"
import Grid from "@mui/material/Grid"
import MDBox from "components/MDBox"
import DashboardLayout from "../components/DashboardLayout"
import DashboardNavbar from "../components/DashboardNavbar"
import HeadingInfoCard from "../components/HeadingInfoCard"
import Notification, { InvoiceList } from "./components/Notification"
import DetectionList from "./components/DetectionList"
import FacilityList, { FacilityData } from "./components/FacilityList"
import { ManageEstateData } from "../manage/estate"
import { ManageCatvData } from "../manage/catv"
import { FacilityLogItem } from "./components/DetectionList"

import { useNavigate } from "react-router-dom"
import { useLoading } from "../LoadingContext"
import { getInfo } from "../../api/info"
import { getSummary } from "../../api/summary"
import { getDetectionList } from "../../api/detection_list"
import { getFacilityList } from "../../api/facility_list"
import { getManageEstateList } from "../../api/manage_estate_list"
import { getManageCatvList } from "api/manage_catv_list"

import { useConnectionError } from "../ConnectionErrorContext"

function Home(): React.ReactElement {
  const [notification, setNotification] = useState<string>("")
  const [facilityCount, setFacilityCount] = useState<string>("")
  const [detectionCount, setDetectionCount] = useState<string>("")
  const [alertCount, setAlertCount] = useState<string>("")
  const [detections, setDetections] = useState<FacilityLogItem[]>([])
  const [facilities, setFacilities] = useState<FacilityData[]>([])
  const [catvs, setCatvs] = useState<ManageCatvData[]>([])
  const [estates, setEstates] = useState<ManageEstateData[]>([])
  const [filter, setFilter] = useState<number>(0)
  const [invoiceCount, setInvoiceCount] = useState<number>(0)
  const [invoicePocCount, setInvoicePocCount] = useState<number>(0)
  const [invoiceList, setInvoiceList] = useState<InvoiceList[]>([])
  const { showConnectionError } = useConnectionError()

  const { startLoading, stopLoading } = useLoading()
  const navigate = useNavigate()

  if (localStorage.getItem("session_code") == "") {
    navigate("/login")
    return <></>
  }

  const handleSummaryClick = (type: number) => {
    setFilter(type) // クリックされたSummaryに応じてフィルタを更新
  }

  useEffect(() => {
    // 開発者アカウントでの閲覧を可能とする
    const auth = localStorage.getItem("account_type")
    let catv_id = ""
    let estate_id = ""
    if (auth === "service") {
      const selectedCatv = localStorage.getItem("show_mode_catv")
      if (selectedCatv !== null && selectedCatv !== "" && selectedCatv !== "-") {
        catv_id = selectedCatv.split("|")[1]
      }
      const selectedEstate = localStorage.getItem("show_mode_estate")
      if (selectedEstate !== null && selectedEstate !== "" && selectedEstate !== "-") {
        estate_id = selectedEstate.split("|")[1]
      }
    }

    const fetchInfo = async () => {
      try {
        startLoading()
        const infoData = await getInfo()
        setNotification(infoData.body)
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          navigate("/login")
        } else if (!error.response) {
          showConnectionError(true)
        }
      } finally {
        stopLoading()
      }
    }
    const fetchSummary = async () => {
      try {
        startLoading()
        const summaryData = await getSummary(0, "")
        setFacilityCount(summaryData.facility_count + "件 (" + summaryData.medical_record_count + "戸)")
        setDetectionCount(summaryData.detection_count + "件")
        setAlertCount(summaryData.alert_count + "件")
        if (summaryData.monthly_invoice_list.length >= 1) {
          const latestInvoice = summaryData.monthly_invoice_list[summaryData.monthly_invoice_list.length - 1]
          // 請求対象数には PoC を含める
          setInvoiceCount(latestInvoice.medical_record_count + latestInvoice.medical_record_count_poc)
          setInvoicePocCount(latestInvoice.medical_record_count_poc)
        }
        setInvoiceList(summaryData.monthly_invoice_list)
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          navigate("/login")
        } else if (!error.response) {
          showConnectionError(true)
        }
      } finally {
        stopLoading()
      }
    }
    const fetchDetection = async () => {
      try {
        startLoading()
        const detectionData = await getDetectionList()
        setDetections(detectionData.items)
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          navigate("/login")
        } else if (!error.response) {
          showConnectionError(true)
        }
      } finally {
        stopLoading()
      }
    }
    const fetchFacility = async () => {
      try {
        startLoading()
        const facilityData = await getFacilityList()
        setFacilities(facilityData.facility_list)
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          navigate("/login")
        } else if (!error.response) {
          showConnectionError(true)
        }
      } finally {
        stopLoading()
      }
    }
    const fetchEstate = async () => {
      const auth = localStorage.getItem("account_type")
      if (auth == "estate") {
        return
      }
      try {
        startLoading()
        if (auth == "catv") {
          const manageEstateData = await getManageEstateList()
          setEstates(manageEstateData.estate_list)
        } else if (auth == "service") {
          if (estate_id != "") {
            return
          }
          if (catv_id == "") {
            const manageCatvData = await getManageCatvList()
            setCatvs(manageCatvData.catv_list)
          } else {
            const manageEstateData = await getManageEstateList()
            setEstates(manageEstateData.estate_list)
          }
        }
      } catch (error: any) {
        if (error.response && error.response.status === 401) {
          navigate("/login")
        } else if (!error.response) {
          showConnectionError(true)
        }
      } finally {
        stopLoading()
      }
    }
    fetchInfo()
    fetchSummary()
    fetchDetection()
    fetchFacility()
    fetchEstate()
  }, [])

  return (
    <DashboardLayout>
      <MDBox mb={4}>
        <DashboardNavbar absolute={true} />
      </MDBox>
      <MDBox py={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {/* PoC数を引数に追加 */}
            <Notification
              content={notification}
              invoiceCount={invoiceCount}
              invoicePocCount={invoicePocCount}
              invoiceList={invoiceList}
            />
          </Grid>
        </Grid>
      </MDBox>
      <MDBox py={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={1.5}>
              <HeadingInfoCard
                icon="domain"
                title="施設数(入居者数)"
                count={facilityCount}
                onClick={() => {
                  handleSummaryClick(1)
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={1.5}>
              <HeadingInfoCard
                color="warning"
                icon="notificationsNone"
                title="不在検知件数"
                count={detectionCount}
                onClick={() => {
                  handleSummaryClick(2)
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={1.5}>
              <HeadingInfoCard
                color="primary"
                icon="reportProblem"
                title="機器エラー数"
                count={alertCount}
                onClick={() => {
                  handleSummaryClick(3)
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <DetectionList
              detections={detections
                // 条件に合わないデータを除外
                .filter((item) => !(item.device_state === 1 && item.detection === 1))
                // 残りのデータをlog_create_dateの古い順にソート
                .sort((a, b) => a.log_create_date - b.log_create_date)}
            />
          </Grid>
        </Grid>
      </MDBox>
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FacilityList facilities={facilities} catvs={catvs} estates={estates} filter={filter} />
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  )
}

export default Home
